import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Inbox } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/Skeletons';
import { NotificationItem } from '@/components/NotificationItem';
import { NotificationTabs } from '@/components/NotificationTabs';
import { useNotifications, useUnreadCount } from '@/lib/queries';
import { useNotificationsView } from '@/lib/use-notifications-view';
import {
  NOTIFICATION_TABS,
  notificationEmptyCopy,
  notificationsSearch,
  type NotificationTabId,
} from '@/lib/notification-categories';
import { cn } from '@/lib/utils';

function formatBadgeCount(n: number): string {
  if (n > 99) return '99+';
  return String(n);
}

export function NotificationsBell() {
  const unreadCountQ = useUnreadCount();
  // Keeps the All list observed (and so cached and kept fresh) while the panel
  // is closed. The panel's own All view shares this exact query, and every
  // other tab uses it as placeholder data while its own request loads — so the
  // panel opens populated instead of on a spinner.
  useNotifications(30);

  // Controlled open state so we can dismiss the popover when a row is
  // clicked. Radix auto-closes on outside click + Escape; this just adds
  // close-on-activate, which the user expects from a notification panel.
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const unread = unreadCountQ.data ?? 0;
  const hasUnread = unread > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={
            hasUnread
              ? `Notifications, ${unread} unread`
              : 'Notifications'
          }
          className="relative h-8 w-8 px-0 text-muted-foreground hover:text-foreground"
        >
          <Bell className="h-4 w-4" />
          {hasUnread ? (
            <span
              className={cn(
                'absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-tint-critical-fg px-1 text-3xs font-semibold leading-none text-background ring-2 ring-background',
                'h-4',
              )}
            >
              {formatBadgeCount(unread)}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      {/* max-w caps the popover to the viewport so it never pokes off the left
          edge on a 360px phone. The max-h uses the space Radix measures as
          available, so the extra tab + switch chrome can't push the panel off
          a short landscape screen — the list inside is what scrolls.
          z-[1000000000] beats sonner's toaster (999999999): toasts stack in this
          same corner, and a "Reply sent" toast would otherwise cover the panel
          the agent just opened (hovering it also pauses its timer). */}
      <PopoverContent
        ref={contentRef}
        align="end"
        sideOffset={8}
        aria-label="Notifications"
        tabIndex={-1}
        // Radix would focus the first non-link tabbable — "Mark all read".
        // Enter pressed twice on the bell would then clear every unread flag,
        // which now carry a deliberate "come back to this" meaning. Focus the
        // panel itself instead.
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          contentRef.current?.focus();
        }}
        // The app's single-letter shortcuts listen on `window` and only skip
        // text inputs, so they'd otherwise fire from inside the panel. React
        // stops the event before it reaches `window`; modified keys (⌘K) and
        // Escape (a named key, handled by Radix) are unaffected.
        onKeyDown={(e) => {
          if (!e.ctrlKey && !e.metaKey && !e.altKey && e.key.length === 1) e.stopPropagation();
        }}
        className="z-[1000000000] flex w-[360px] max-w-[calc(100vw-1rem)] max-h-[min(36rem,var(--radix-popover-content-available-height))] flex-col p-0"
      >
        {/* Mounted only while open, so the tab, the switch and any pinned rows
            reset to "All / off" each time the bell is opened. */}
        <NotificationsPanel onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

function NotificationsPanel({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<NotificationTabId>('all');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const view = useNotificationsView({ limit: 30, tab, unreadOnly, onNavigate: onClose });

  const tabLabel = NOTIFICATION_TABS.find((t) => t.id === tab)?.label ?? '';
  const empty = notificationEmptyCopy(tab, unreadOnly);

  return (
    <>
      <div className="flex shrink-0 items-center justify-between border-b border-border/70 px-3 py-2">
        <span className="text-sm font-semibold">Notifications</span>
        <button
          type="button"
          disabled={!view.canMarkAll || view.markAllPending}
          onClick={() => {
            view.markAllRead();
            // On All everything is now read, so close like it always did. On
            // a category tab the other tabs still have work — stay open.
            if (tab === 'all') onClose();
          }}
          className={cn(
            'text-xs font-medium transition-colors',
            view.canMarkAll
              ? 'cursor-pointer text-foreground hover:underline'
              : 'cursor-not-allowed text-muted-foreground',
          )}
        >
          {tab === 'all' ? 'Mark all read' : `Mark ${tabLabel.toLowerCase()} read`}
        </button>
      </div>

      <NotificationTabs
        tab={tab}
        onTabChange={setTab}
        unreadOnly={unreadOnly}
        onUnreadOnlyChange={setUnreadOnly}
        unreadByTab={view.unreadByTab}
        density="compact"
      >
        {view.showSkeleton ? (
          <div className="px-4 py-10 text-center text-xs text-muted-foreground">Loading…</div>
        ) : view.isError && view.rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted-foreground">
            Couldn&apos;t load notifications.
          </div>
        ) : view.rows.length === 0 ? (
          <EmptyState
            icon={<Inbox className="h-4 w-4" />}
            title={empty.title}
            subtitle={empty.subtitle}
            className="py-10"
          />
        ) : (
          <ul className="divide-y divide-border/50">
            {view.rows.map((n) => (
              <NotificationItem
                key={n.id}
                notification={n}
                density="compact"
                onActivate={view.activate}
                onToggleRead={view.toggleRead}
              />
            ))}
          </ul>
        )}
      </NotificationTabs>

      {/* Footer link to the full history page — carries the current view. */}
      <div className="shrink-0 border-t border-border/70 px-3 py-2 text-center">
        <Link
          to={`/notifications${notificationsSearch(tab, unreadOnly)}`}
          onClick={onClose}
          className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
        >
          View all notifications
        </Link>
      </div>
    </>
  );
}
